import React, { useState } from 'react';
import CustomInput from '../../components/form/CustomInput';
import { Link, useNavigate } from 'react-router-dom';
import CustomButtonConfirm from '../../components/form/CustomButtonConfirm';
import { loginSuccess } from '../../store/auth/login/action';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { useDispatch } from 'react-redux';
import { resetPassword } from '../../helpers/configRoutes/backend_helper';
import { postLoginToken } from '../../helpers/configRoutes/api_helper';

const Login = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [forgotPassword, setForgotPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const validation = useFormik({
    initialValues: {
      email: '',
      password: '',
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .email('Correo electrónico no válido')
        .required('Ingresa tu correo electrónico'),
      password: Yup.string().when('forgotPassword', {
        is: false,
        then: Yup.string().required('Ingresa tu contraseña'),
      }),
    }),
    onSubmit: async (values, { setErrors }) => {
      setLoading(true);
      if (forgotPassword) {
        const response = await resetPassword(values);
        if (response.status === true) {
          setForgotPassword(false);
        }
      } else {
        const response = await postLoginToken(values);
        if (response.status === true) {
          dispatch(loginSuccess(response));
          navigate('/inicio');
        } else {
          if (response.errors?.length) {
            setErrors(response.errors);
          }
        }
      }
      setLoading(false);
    },
  });

  return (
    <div className="relative w-screen h-screen overflow-hidden flex items-center justify-center select-none bg-gradient-to-br from-slate-900 via-slate-800 to-fr-primary">
      {/* Fondo decorativo: blobs animados */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-32 -left-32 w-[28rem] h-[28rem] bg-fr-primary/40 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute top-1/3 -right-40 w-[32rem] h-[32rem] bg-cyan-500/30 rounded-full blur-3xl animate-pulse [animation-delay:1s]"></div>
        <div className="absolute -bottom-40 left-1/4 w-[30rem] h-[30rem] bg-violet-500/30 rounded-full blur-3xl animate-pulse [animation-delay:2s]"></div>

        {/* Grid sutil */}
        <div
          className="absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.4) 1px, transparent 1px)',
            backgroundSize: '40px 40px',
          }}
        ></div>
      </div>

      {/* Card principal con efecto glass */}
      <div className="relative z-10 w-[95%] md:w-[85%] max-w-[1050px] rounded-3xl overflow-hidden border border-white/10 shadow-[0_25px_80px_-15px_rgba(0,0,0,0.6)] backdrop-blur-xl bg-white/5">
        <div className="flex flex-col md:flex-row">
          {/* Panel lateral */}
          <div className="hidden md:flex relative w-1/2 items-center justify-center bg-gradient-to-br from-fr-dark via-slate-900 to-fr-primary overflow-hidden p-10">
            {/* Ornamentos */}
            <div className="absolute -top-20 -left-20 w-72 h-72 bg-white/5 rounded-full blur-2xl"></div>
            <div className="absolute -bottom-24 -right-16 w-80 h-80 bg-fr-primary/30 rounded-full blur-3xl"></div>

            <div className="relative z-10 flex flex-col items-center text-center">
              <img
                src="/images/lexsys_logo.svg"
                alt="Login Illustration"
                className="max-h-[420px] object-contain drop-shadow-2xl mb-6 transition-transform duration-700 hover:scale-105"
              />
              <h3 className="text-white text-2xl font-bold tracking-tight">
                FactuRadar
              </h3>
              <p className="text-white/70 text-sm mt-2 max-w-[280px] leading-relaxed">
                Tu radar inteligente para mantener tus facturas siempre bajo control.
              </p>

              {/* Dots decorativos */}
              <div className="flex gap-2 mt-8">
                <span className="w-2 h-2 rounded-full bg-white/40"></span>
                <span className="w-2 h-2 rounded-full bg-white/70"></span>
                <span className="w-6 h-2 rounded-full bg-white"></span>
              </div>
            </div>
          </div>

          {/* Formulario */}
          <div className="w-full md:w-1/2 p-8 sm:p-10 md:p-12 flex flex-col justify-center bg-white/95 backdrop-blur-sm">
            {/* Logo / brand pequeño */}
            <h2 className="text-4xl font-extrabold text-fr-dark tracking-tight mb-2">
              Bienvenido 
            </h2>
            <p className="text-fr-muted mb-8 text-sm">
              {forgotPassword
                ? 'Recupera el acceso a tu cuenta'
                : 'Inicia sesión para acceder a FactuRadar'}
            </p>

            <form
              onSubmit={e => {
                e.preventDefault();
                validation.handleSubmit();
              }}
              className="space-y-5"
            >
              <div className="group transition-all">
                <CustomInput
                  label="Email"
                  type="email"
                  name="email"
                  labelClassName="text-sm font-semibold text-fr-dark"
                  error={validation.errors.email}
                  value={validation.values.email}
                  handleChange={validation.handleChange}
                />
              </div>

              <div className="group transition-all">
                <CustomInput
                  label="Password"
                  type="password"
                  name="password"
                  labelClassName="text-sm font-semibold text-fr-dark"
                  error={validation.errors.password}
                  value={validation.values.password}
                  handleChange={validation.handleChange}
                />
              </div>

              <div className="pt-2">
                <CustomButtonConfirm
                  loading={loading}
                  textLoad={'Procesando...'}
                  type="success"
                  className="w-full py-3 rounded-xl bg-gradient-to-r from-fr-primary to-fr-hover hover:from-fr-hover hover:to-fr-primary text-white font-semibold tracking-wide shadow-lg shadow-fr-primary/30 hover:shadow-fr-primary/50 transition-all duration-300 hover:-translate-y-0.5"
                  text={forgotPassword ? 'Enviar enlace de recuperación' : 'Iniciar sesión'}
                />
              </div>

              <div className="flex items-center gap-3 my-2">
                <div className="flex-1 h-px bg-gradient-to-r from-transparent via-fr-muted/30 to-transparent"></div>
                <span className="text-xs uppercase tracking-widest text-fr-muted">
                  o
                </span>
                <div className="flex-1 h-px bg-gradient-to-r from-transparent via-fr-muted/30 to-transparent"></div>
              </div>

              <div className="text-center">
                <Link
                  to="/forgotPassword"
                  className="text-sm text-fr-primary font-medium hover:text-fr-hover transition-colors relative inline-block group"
                >
                  ¿Olvidaste tu contraseña?
                  <span className="absolute left-0 -bottom-0.5 w-0 h-0.5 bg-fr-primary group-hover:w-full transition-all duration-300"></span>
                </Link>
              </div>
            </form>

            <p className="text-center text-xs text-fr-muted mt-8">
              © {new Date().getFullYear()} Lexsys — Todos los derechos reservados
            </p>
          </div>
        </div>
      </div>

      {/* Animación wave para el emoji */}
      <style>{`
        @keyframes wave {
          0%, 60%, 100% { transform: rotate(0deg); }
          10%, 30% { transform: rotate(14deg); }
          20% { transform: rotate(-8deg); }
          40% { transform: rotate(-4deg); }
          50% { transform: rotate(10deg); }
        }
        .animate-wave {
          transform-origin: 70% 70%;
          animation: wave 2.5s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default Login;
